// RUNTIME RULESET (config-as-data, M1). The single registry of every TUNABLE block in the game. The canonical values
// still live in their original modules (config.ts, inventory.ts, ...) and game code keeps importing them unchanged --
// this module MUTATES those same objects IN PLACE so a server/admin patch retunes the live game without a redeploy and,
// for "live" fields, without a restart. Nothing is reassigned: every importer keeps its live reference, and call-time
// reads pick up the new value on the next frame/action.
//
// Flow: boot fetches a sparse PATCH (only fields that differ from the shipped defaults) from the authority and calls
// applyRuleset(patch); a later live config push calls it again. DEFAULTS is a snapshot captured BEFORE any overlay so
// the /admin page can diff against it and reset fields. See the M1 spec for the live/reload/structural classification.

import { CAMERA, MOVE, PLAYER, SURVIVAL, FORAGE, ANIMALS, TOOLS, NODES, BUILD, UPKEEP, RESPAWN, DAYNIGHT, WATER, GRADE, ASSETS, SCATTER, TORCH, TORCH_DRAIN, DURABILITY, REPAIR_COST, TERRAIN, CLIMATE, BIOMES } from './config';
import { ITEMS, STACK_LIMIT } from './game/inventory';
import { CONSUMABLES } from './game/consumables';
import { CRAFTABLES } from './game/craftables';
import { BALLISTICS } from './engine/ballistics';
import { RECIPES } from './ui/crafting';
import { RESEARCH } from './game/research';
import { WEAPONS, AMMO_STATS } from './game/weapons';

// Each key points at the LIVE exported block (same object identity the game reads every frame). Grouped by where the
// edit takes effect: see the M1 classification table (live = instant, reload = rebuild rules + keep world, structural
// = regenerate the world). The /admin UI is generated from this registry.
export const RULESET = {
  // --- player & combat (live) ---
  camera: CAMERA, move: MOVE, player: PLAYER, survival: SURVIVAL, ballistics: BALLISTICS, tools: TOOLS, torch: TORCH,
  // --- world entities (mixed: scalars live, pool sizes/counts reload) ---
  animals: ANIMALS, forage: FORAGE, nodes: NODES, assets: ASSETS, scatter: SCATTER,
  // --- build / economy (reload) ---
  build: BUILD, upkeep: UPKEEP, respawn: RESPAWN, durability: DURABILITY, torchDrain: TORCH_DRAIN, repairCost: REPAIR_COST,
  // --- atmosphere (live) ---
  daynight: DAYNIGHT, water: WATER, grade: GRADE,
  // --- items & recipes (live scalars / reload structure) ---
  items: ITEMS, stackLimit: STACK_LIMIT, consumables: CONSUMABLES, craftables: CRAFTABLES, recipes: RECIPES, research: RESEARCH,
  weapons: WEAPONS, ammoStats: AMMO_STATS,
  // --- world generation (structural -> needs regenerate) ---
  terrain: TERRAIN, climate: CLIMATE, biomes: BIOMES,
  // research: reserved for M5 (the tech tree) -- key added when that system lands.
};

export type Ruleset = typeof RULESET;
// A sparse override: any subset of blocks, each a partial of that block. This is what the admin authors + D1 stores.
export type RulesetPatch = { [K in keyof Ruleset]?: Partial<Ruleset[K]> };

// The shipped defaults, deep-cloned BEFORE any patch is applied. The /admin diffs against this (to store only changes)
// and resets fields to it. Captured at module load -- importing this module must happen before applyRuleset runs.
export const DEFAULTS: Ruleset = structuredClone(RULESET);

// Deep-merge a sparse patch INTO a live block. Nested plain objects recurse; scalars AND arrays replace whole (an admin
// edits a list as a unit -- per-index array merge is error-prone). Mutates `target` in place; never reassigns it.
function mergeInto(target: Record<string, unknown>, patch: Record<string, unknown>): void {
  for (const k of Object.keys(patch)) {
    const pv = patch[k], tv = target[k];
    if (pv && typeof pv === 'object' && !Array.isArray(pv) && tv && typeof tv === 'object' && !Array.isArray(tv)) {
      mergeInto(tv as Record<string, unknown>, pv as Record<string, unknown>);
    } else {
      target[k] = pv; // scalar or array -> overwrite
    }
  }
}

// Overlay a sparse patch onto the live ruleset (per-block deep merge), then refresh any precomputed/derived values.
// Safe to call at boot (after fetching the patch) and again on a live config push. A null/empty patch is a no-op.
export function applyRuleset(patch: RulesetPatch | null | undefined): void {
  if (!patch) return;
  for (const key of Object.keys(patch) as Array<keyof Ruleset>) {
    const block = RULESET[key] as unknown;
    const sub = patch[key] as unknown;
    if (Array.isArray(block) && Array.isArray(sub)) { block.length = 0; block.push(...(sub as unknown[])); } // array block (recipes/biomes): replace CONTENTS, keep the live binding
    else if (block && sub && typeof block === 'object') mergeInto(block as Record<string, unknown>, sub as Record<string, unknown>);
  }
  recomputeDerived();
}

// Restore every block to its shipped default (admin "reset all" / rollback to a clean base before re-applying a patch).
export function resetToDefaults(): void {
  for (const key of Object.keys(RULESET) as Array<keyof Ruleset>) {
    const block = RULESET[key] as Record<string, unknown>;
    const def = structuredClone(DEFAULTS[key]) as Record<string, unknown> | unknown[];
    if (Array.isArray(block) && Array.isArray(def)) { block.length = 0; block.push(...def); }
    else if (block && typeof block === 'object') { for (const k of Object.keys(block)) delete block[k]; Object.assign(block, def); }
  }
  recomputeDerived();
}

// The sparse difference of one value from its default (the changed leaves only), or undefined if equal. Arrays +
// scalars compare/replace whole; objects recurse. Used to turn the live-edited ruleset back into a minimal patch.
function diffValue(cur: unknown, def: unknown): unknown {
  if (cur === null || typeof cur !== 'object' || Array.isArray(cur)) return JSON.stringify(cur) !== JSON.stringify(def) ? cur : undefined;
  const d = (def ?? {}) as Record<string, unknown>;
  const out: Record<string, unknown> = {}; let changed = false;
  for (const k of Object.keys(cur as Record<string, unknown>)) {
    const sub = diffValue((cur as Record<string, unknown>)[k], d[k]);
    if (sub !== undefined) { out[k] = sub; changed = true; }
  }
  return changed ? out : undefined;
}

// The current live ruleset as a SPARSE patch over the shipped defaults (only changed fields). This is what Save stores.
export function diffFromDefaults(): RulesetPatch {
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(RULESET) as Array<keyof Ruleset>) {
    const d = diffValue(RULESET[key], DEFAULTS[key]);
    if (d !== undefined) patch[key] = d;
  }
  return patch as RulesetPatch;
}

// Where the locally-saved admin patch lives (single-player + offline). The /admin Save writes here until the server
// endpoint exists; the server path (JoinInfo) will later overlay on top of this. Keep the key stable.
const PATCH_KEY = 'oxide.configPatch';

// Save the CURRENT live ruleset (whatever the panel/knobs set) as the persisted patch. Replaces the stored patch with
// the full current diff -- so lowering a value back toward default correctly drops it. Returns the stored patch.
export function persistRuleset(): RulesetPatch {
  const patch = diffFromDefaults();
  try { localStorage.setItem(PATCH_KEY, JSON.stringify(patch)); } catch { /* storage full/blocked */ }
  return patch;
}

// Boot: apply the locally-saved patch over the defaults. Bad/absent/blocked storage just leaves pure defaults.
export function loadLocalRuleset(): void {
  try { const raw = localStorage.getItem(PATCH_KEY); if (raw) applyRuleset(JSON.parse(raw) as RulesetPatch); }
  catch { try { localStorage.removeItem(PATCH_KEY); } catch { /* */ } } // a corrupt patch can't brick boot: drop it, run on pure defaults
}

// Persist + apply a patch (the /admin Save target until the server endpoint lands). MERGES into any existing saved
// patch so a partial save (one panel/field) never wipes other overrides. Returns the full accumulated patch.
export function saveLocalRuleset(patch: RulesetPatch): RulesetPatch {
  let cur: Record<string, unknown> = {};
  try { cur = JSON.parse(localStorage.getItem(PATCH_KEY) || '{}') as Record<string, unknown>; } catch { cur = {}; }
  mergeInto(cur, patch as Record<string, unknown>);
  try { localStorage.setItem(PATCH_KEY, JSON.stringify(cur)); } catch { /* storage full/blocked */ }
  applyRuleset(patch);
  return cur as RulesetPatch;
}

// Recompute values that are DERIVED from tunables (so editing a source field updates the derivation). Wired up per
// block as the migration removes module-load captures (M1 step 3); a stub today since no live block has a consumer
// that captures a derived value at import yet. Known future hooks: grass colour linears, MOVE.accel/decel.
export function recomputeDerived(): void {
  // intentionally empty for step 0 -- see the M1 spec "read-pattern rule".
}
